import {
  isEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  pdfjs,
} from 'react-pdf';
import {
  FillableFormSession,
} from 'views/EMR/components/FillableForm/types';

const LOG_PREFIX = '[FillableForm PDF]';

export type PdfUrlType = 'missing' | 'data' | 'blob' | 'remote' | 'unknown';

export type PdfFailureStage = 'source' | 'load';

export type WorkerProbeResult = {
  status?: number;
  contentType?: string | null;
  contentLength?: string | null;
  looksLikeHtml?: boolean;
  bodyPreview?: string;
  fetchError?: string;
};

export type PdfFailureContext = {
  formId?: number | null;
  pageCount?: number;
  pdfUrlType: PdfUrlType;
  pdfUrlHost: string | null;
  workerProbe?: WorkerProbeResult | null;
};

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
}

export function getPdfUrlType(url?: string | null): PdfUrlType {
  if (isNil(url) || isEmptyString(url)) {
    return 'missing';
  }
  if (url?.startsWith('data:')) {
    return 'data';
  }
  if (url?.startsWith('blob:')) {
    return 'blob';
  }
  if (url?.startsWith('http://') || url?.startsWith('https://')) {
    return 'remote';
  }
  return 'unknown';
}

export function getPdfUrlHost(url?: string | null): string | null {
  if (isNil(url) || !url.startsWith('http')) {
    return null;
  }
  try {
    return new URL(url).host;
  } catch {
    return 'invalid';
  }
}

export function getPdfFailureContext(session: FillableFormSession): Omit<PdfFailureContext, 'workerProbe'> {
  return {
    formId: session.formId,
    pageCount: session.pages?.length,
    pdfUrlType: getPdfUrlType(session.pdfUrl),
    pdfUrlHost: getPdfUrlHost(session.pdfUrl),
  };
}

export async function probeWorkerAsset(workerSrc: string): Promise<WorkerProbeResult> {
  try {
    const response = await fetch(workerSrc, { cache: 'no-store' });
    const contentType = response.headers.get('content-type');
    const contentLength = response.headers.get('content-length');
    const body = await response.text();
    const trimmedBody = body.trimStart();

    return {
      status: response.status,
      contentType,
      contentLength,
      looksLikeHtml: trimmedBody.startsWith('<'),
      bodyPreview: body.slice(0, 120),
    };
  } catch (error) {
    const fetchError = toError(error);
    return {
      fetchError: fetchError.message,
    };
  }
}

export function describePdfError(error: unknown, workerSrc: string): string {
  const { message } = toError(error);

  if (message.includes('dynamically imported module') && !isNil(workerSrc) && workerSrc !== '') {
    return `PDF worker failed to load (${workerSrc})`;
  }
  if (message.includes('non-JavaScript MIME type')) {
    return `PDF worker returned HTML instead of JavaScript (${workerSrc})`;
  }
  if (message.includes('pdf.worker.mjs')) {
    return 'PDF worker is misconfigured (relative worker path)';
  }
  if (!isNil(message) && message.trim() !== '') {
    return message;
  }
  return 'Error loading PDF';
}

function logPdfFailure(
  stage: PdfFailureStage,
  error: unknown,
  context: PdfFailureContext,
): void {
  const err = toError(error);
  const {
    workerSrc,
  } = pdfjs.GlobalWorkerOptions;

  console.error(LOG_PREFIX, {
    stage,
    name: err.name,
    message: err.message,
    stack: err.stack,
    cause: err.cause instanceof Error ? err.cause.message : err.cause,
    workerSrc,
    pdfjsVersion: pdfjs.version,
    userAgent: navigator.userAgent,
    location: window.location.href,
    ...context,
  });
}

export function handlePdfSourceError(
  error: unknown,
  session: FillableFormSession,
): string {
  const context = getPdfFailureContext(session);
  logPdfFailure('source', error, context);
  return describePdfError(error, pdfjs.GlobalWorkerOptions.workerSrc);
}

export async function handlePdfLoadError(
  error: unknown,
  session: FillableFormSession,
): Promise<string> {
  const {
    workerSrc,
  } = pdfjs.GlobalWorkerOptions;
  const context = getPdfFailureContext(session);
  const workerProbe = !isNil(workerSrc) && workerSrc !== ''
    ? await probeWorkerAsset(workerSrc)
    : null;

  logPdfFailure('load', error, {
    ...context,
    workerProbe,
  });

  return describePdfError(error, workerSrc);
}
